import React, { ReactNode, useMemo } from 'react';
import { createNullableContext } from '../context/create-nullable-context';
import { Locale, useLocale } from '../i18n/hooks/use-locale';

type FormatterInstances = {
  dateShortFormatter: Intl.DateTimeFormat;
  dateMediumFormatter: Intl.DateTimeFormat;
  dateLongFormatter: Intl.DateTimeFormat;
  numberFormatter: Intl.NumberFormat;
  numberPrecisionFormatter: Intl.NumberFormat;
  numberCurrencyFormatter: Intl.NumberFormat;
  integerFormatter: Intl.NumberFormat;
};

const initializer = (locale: Locale): FormatterInstances => {
  return {
    dateShortFormatter: new Intl.DateTimeFormat(locale, { dateStyle: 'short' }),
    dateMediumFormatter: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }),
    dateLongFormatter: new Intl.DateTimeFormat(locale, { dateStyle: 'long' }),
    numberFormatter: new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    numberPrecisionFormatter: new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }),
    integerFormatter: new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }),
    numberCurrencyFormatter: new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    }),
  };
};

type Props = {
  children: ReactNode;
};

const [FormatterInstanceContext, useFormatterInstanceContext] =
  createNullableContext<FormatterInstances>('FormatterInstanceContext');

export const FormatterInstanceProvider: React.FC<Props> = ({ children }) => {
  const locale = useLocale();

  const instance = useMemo(() => initializer(locale), [locale]);
  return <FormatterInstanceContext.Provider value={instance}>{children}</FormatterInstanceContext.Provider>;
};

export { useFormatterInstanceContext };
